import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Icon,
  useColorModeValue,
  SimpleGrid,
  List,
  ListItem,
  ListIcon,
  Divider,
} from '@chakra-ui/react';
import { FaChartLine, FaCoins, FaLock, FaRocket, FaServer, FaBolt, FaChartBar, FaCheckCircle, FaNetworkWired, FaShieldAlt, FaCubes, FaMicrochip } from 'react-icons/fa';
import { MdSecurity, MdAccountBalance } from 'react-icons/md';
import Footer from '../components/Footer';

const HowItWorks = () => {
  const bgColor = useColorModeValue('gray.800', 'gray.900');
  const cardBg = useColorModeValue('gray.700', 'gray.800');

  const steps = [
    {
      icon: FaRocket,
      title: 'Join HODL',
      description: 'Participate in the HODL community and start earning rewards.',
    },
    {
      icon: FaCoins,
      title: 'Hold Tokens',
      description: 'Simply hold your HODL tokens to be eligible for rewards.',
    },
    {
      icon: FaChartLine,
      title: 'Earn Rewards',
      description: 'Receive rewards automatically based on your token holdings.',
    },
    {
      icon: FaCoins,
      title: 'Compound Growth',
      description: 'Watch your earnings grow through our compound reward system.',
    },
  ];

  const features = [
    {
      icon: MdSecurity,
      title: 'Security First',
      description: 'Our smart contracts are audited and secured by leading security firms.',
    },
    {
      icon: MdAccountBalance,
      title: 'Transparent',
      description: 'All transactions and rewards are visible on the blockchain.',
    },
  ];

  const blockchainFeatures = [
    {
      icon: FaServer,
      title: 'Internet Computer (ICP)',
      description: 'Built on the Internet Computer, the world\'s fastest and most scalable blockchain. ICP offers unlimited scalability, low latency, and high security for our platform.',
    },
    {
      icon: FaBolt,
      title: 'Solana Integration',
      description: 'Leveraging Solana\'s high-performance blockchain for fast transactions and low fees. Solana provides the speed and efficiency needed for seamless token operations.',
    },
  ];

  const integrationFeatures = [
    {
      icon: FaNetworkWired,
      title: 'Cross-Chain Innovation',
      description: 'Our unique integration of ICP and Solana creates a powerful synergy. ICP provides the backend infrastructure and smart contract execution, while Solana handles the token operations and rewards distribution.',
    },
    {
      icon: FaShieldAlt,
      title: 'Enhanced Security',
      description: 'By leveraging both ICP\'s secure canister system and Solana\'s robust validator network, we create multiple layers of security for your assets and transactions.',
    },
  ];

  const icpPowerFeatures = [
    {
      icon: FaCubes,
      title: 'Canister Architecture',
      description: 'ICP\'s canister architecture allows for modular, upgradable smart contracts that can be deployed and updated without downtime.',
    },
    {
      icon: FaMicrochip,
      title: 'WebAssembly Execution',
      description: 'Smart contracts run on WebAssembly, providing near-native performance and enabling complex computations with minimal cost.',
    },
  ];

  return (
    <Box pt={20} minH="100vh" bg={bgColor}>
      <Container maxW="container.xl" py={10}>
        <VStack spacing={8} align="stretch">
          <Box textAlign="center" mb={10}>
            <Heading
              as="h1"
              size="2xl"
              bgGradient="linear(to-r, cyan.400, blue.500)"
              bgClip="text"
              mb={4}
            >
              How HODL Works
            </Heading>
            <Text fontSize="xl" color="gray.400">
              A simple and secure way to earn rewards while holding your tokens
            </Text>
          </Box>

          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={8} mb={10}>
            {steps.map((step, index) => (
              <Box
                key={index}
                bg={cardBg}
                p={6}
                borderRadius="lg"
                boxShadow="xl"
                transition="transform 0.2s"
                _hover={{ transform: 'translateY(-5px)' }}
              >
                <VStack spacing={4} align="start">
                  <Icon as={step.icon} w={8} h={8} color="cyan.400" />
                  <Heading size="md" color="white">
                    {step.title}
                  </Heading>
                  <Text color="gray.400">{step.description}</Text>
                </VStack>
              </Box>
            ))}
          </SimpleGrid>

          <Box bg={cardBg} p={8} borderRadius="lg" boxShadow="xl">
            <Heading size="lg" mb={6} color="white">
              Key Features
            </Heading>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
              {features.map((feature, index) => (
                <HStack key={index} align="start" spacing={4}>
                  <Icon as={feature.icon} w={6} h={6} color="cyan.400" />
                  <Box>
                    <Heading size="md" color="white" mb={2}>
                      {feature.title}
                    </Heading>
                    <Text color="gray.400">{feature.description}</Text>
                  </Box>
                </HStack>
              ))}
            </SimpleGrid>
          </Box>

          <Divider borderColor="gray.700" />

          <Box bg={cardBg} p={8} borderRadius="lg" boxShadow="xl">
            <Heading size="lg" mb={6} color="white">
              Powered by Advanced Blockchains
            </Heading>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
              {blockchainFeatures.map((feature, index) => (
                <HStack key={index} align="start" spacing={4}>
                  <Icon as={feature.icon} w={6} h={6} color="cyan.400" />
                  <Box>
                    <Heading size="md" color="white" mb={2}>
                      {feature.title}
                    </Heading>
                    <Text color="gray.400">{feature.description}</Text>
                  </Box>
                </HStack>
              ))}
            </SimpleGrid>
          </Box>

          <Box bg={cardBg} p={8} borderRadius="lg" boxShadow="xl">
            <Heading size="lg" mb={6} color="white">
              The Power of ICP & Solana Integration
            </Heading>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
              {integrationFeatures.map((feature, index) => (
                <HStack key={index} align="start" spacing={4}>
                  <Icon as={feature.icon} w={6} h={6} color="cyan.400" />
                  <Box>
                    <Heading size="md" color="white" mb={2}>
                      {feature.title}
                    </Heading>
                    <Text color="gray.400">{feature.description}</Text>
                  </Box>
                </HStack>
              ))}
            </SimpleGrid>
            <Box mt={6}>
              <Text color="gray.400">
                The Internet Computer (ICP) provides our platform with:
              </Text>
              <List spacing={3} mt={4} color="gray.400">
                <ListItem>
                  <ListIcon as={FaCheckCircle} color="cyan.400" />
                  Reverse-gas model for seamless user experience
                </ListItem>
                <ListItem>
                  <ListIcon as={FaCheckCircle} color="cyan.400" />
                  WebAssembly-based smart contracts with superior performance
                </ListItem>
                <ListItem>
                  <ListIcon as={FaCheckCircle} color="cyan.400" />
                  Chain-key cryptography for enhanced security
                </ListItem>
                <ListItem>
                  <ListIcon as={FaCheckCircle} color="cyan.400" />
                  Autonomous smart contracts with continuous operation
                </ListItem>
              </List>
            </Box>
          </Box>

          <Box bg={cardBg} p={8} borderRadius="lg" boxShadow="xl">
            <Heading size="lg" mb={6} color="white">
              The Power of Internet Computer
            </Heading>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
              {icpPowerFeatures.map((feature, index) => (
                <HStack key={index} align="start" spacing={4}>
                  <Icon as={feature.icon} w={6} h={6} color="cyan.400" />
                  <Box>
                    <Heading size="md" color="white" mb={2}>
                      {feature.title}
                    </Heading>
                    <Text color="gray.400">{feature.description}</Text>
                  </Box>
                </HStack>
              ))}
            </SimpleGrid>
            <Box mt={6}>
              <Text color="gray.400">
                The Internet Computer revolutionizes blockchain technology with:
              </Text>
              <List spacing={3} mt={4} color="gray.400">
                <ListItem>
                  <ListIcon as={FaCheckCircle} color="cyan.400" />
                  Sub-second finality for instant transaction confirmation
                </ListItem>
                <ListItem>
                  <ListIcon as={FaCheckCircle} color="cyan.400" />
                  Unlimited scalability with no gas fees for end users
                </ListItem>
                <ListItem>
                  <ListIcon as={FaCheckCircle} color="cyan.400" />
                  Interoperability with other blockchains through Chain Key cryptography
                </ListItem>
                <ListItem>
                  <ListIcon as={FaCheckCircle} color="cyan.400" />
                  Advanced governance with Network Nervous System (NNS)
                </ListItem>
                <ListItem>
                  <ListIcon as={FaCheckCircle} color="cyan.400" />
                  Built-in identity and authentication systems
                </ListItem>
              </List>
            </Box>
          </Box>

          <Box bg={cardBg} p={8} borderRadius="lg" boxShadow="xl">
            <Heading size="lg" mb={6} color="white">
              Moonit Integration
            </Heading>
            <VStack align="start" spacing={4}>
              <HStack>
                <Icon as={FaChartBar} w={6} h={6} color="cyan.400" />
                <Heading size="md" color="white">Bonding Curve Launch</Heading>
              </HStack>
              <Text color="gray.400">
                The HODL token will be launched on Moonit using a bonding curve mechanism. This innovative approach ensures fair token distribution and price discovery.
              </Text>
              <Text color="gray.400">
                <strong>Important:</strong> Rewards will begin 24 hours after the token meets the Moonit bonding curve criteria. This waiting period ensures stability and proper token distribution before rewards commence.
              </Text>
              <List spacing={3} color="gray.400">
                <ListItem>
                  <ListIcon as={FaCheckCircle} color="cyan.400" />
                  Token launch on Moonit platform
                </ListItem>
                <ListItem>
                  <ListIcon as={FaCheckCircle} color="cyan.400" />
                  24-hour stabilization period after bonding curve criteria is met
                </ListItem>
                <ListItem>
                  <ListIcon as={FaCheckCircle} color="cyan.400" />
                  Automatic SOL rewards begin after the stabilization period
                </ListItem>
              </List>
            </VStack>
          </Box>

          <Box bg={cardBg} p={8} borderRadius="lg" boxShadow="xl">
            <Heading size="lg" mb={6} color="white">
              Getting Started Checklist
            </Heading>
            <List spacing={4}>
              <ListItem>
                <ListIcon as={FaRocket} color="cyan.400" />
                <Text as="span" color="gray.400">
                  Join the HODL community
                </Text>
              </ListItem>
              <ListItem>
                <ListIcon as={FaLock} color="cyan.400" />
                <Text as="span" color="gray.400">
                  Hold your tokens securely
                </Text>
              </ListItem>
              <ListItem>
                <ListIcon as={FaCoins} color="cyan.400" />
                <Text as="span" color="gray.400">
                  Ensure minimum token holdings
                </Text>
              </ListItem>
              <ListItem>
                <ListIcon as={FaChartLine} color="cyan.400" />
                <Text as="span" color="gray.400">
                  Start earning rewards automatically
                </Text>
              </ListItem>
            </List>
          </Box>
        </VStack>
      </Container>
      <Footer />
    </Box>
  );
};

export default HowItWorks; 